/**
 * Expiry date input with format validation
 */

import React, { useState, useRef } from 'react';
import { TextField } from '@mui/material';
import { formatExpiryDate, parseExpiryDate } from '../services/formatters';

export interface ExpiryDateInputProps {
  month: string;
  year: string;
  onChange: (month: string, year: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
}

export const ExpiryDateInput: React.FC<ExpiryDateInputProps> = ({
  month,
  year,
  onChange,
  onBlur,
  error,
  disabled = false,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  // Tracks when we intentionally cleared the parent (onChange('', '')) so the
  // sync-from-props useEffect below doesn't wipe the display mid-typing.
  const selfClearRef = useRef(false);

  // Sync display value from parent props (e.g. initial load or external reset).
  // Skipped when the component itself just cleared the parent to avoid wiping
  // a partial value the user is still typing.
  React.useEffect(() => {
    if (selfClearRef.current) {
      selfClearRef.current = false;
      return;
    }
    if (month && year) {
      const shortYear = year.slice(-2);
      setDisplayValue(`${month.padStart(2, '0')}/${shortYear}`);
    } else {
      setDisplayValue('');
    }
  }, [month, year]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const formatted = formatExpiryDate(rawValue);

    if (formatted.length <= 5) {
      setDisplayValue(formatted);

      // Only propagate to parent once the full MM/YY is present (5 chars).
      // Any partial input clears parent state immediately so no stale valid
      // date can survive while the textbox shows an incomplete value.
      if (formatted.length === 5) {
        const parsed = parseExpiryDate(formatted);
        if (parsed) {
          onChange(parsed.month, parsed.year);
        }
      } else {
        selfClearRef.current = true;
        onChange('', '');
      }
    }
  };

  const handleBlur = () => {
    onBlur?.();
  };

  // Show error from parent (form submission) only.
  const displayError = error;

  return (
    <div className="field-container align-items-center">
      <div className="label">
        <label className="p-2" data-testid="ExpirationDate" htmlFor="expirationDate">
          Expiration Date
        </label>
        <span className="text-danger" data-testid="ExpirationDate-Astrict-icon">
          *
        </span>
      </div>
      <TextField
        id="expirationDate"
        className="textfield"
        fullWidth
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!displayError}
        helperText={displayError || ' '}
        disabled={disabled}
        autoComplete="cc-exp"
        inputMode="numeric"
        placeholder="MM/YY"
        required
        inputProps={{
          'aria-label': 'Expiry date',
          'aria-required': 'true',
          'aria-invalid': !!displayError,
          'aria-describedby': displayError ? 'expiry-date-error' : undefined,
          maxLength: 5,
        }}
      />
    </div>
  );
};

export default ExpiryDateInput;
